import type { StreamItem } from './remember-moments';

export interface RememberVirtualLayout {
  items: readonly StreamItem[];
  prefix: Float64Array;
  yearIndices: Int32Array;
  indexByKey: ReadonlyMap<string, number>;
  total: number;
}

export interface RememberVirtualMetrics {
  comparisons: number;
}

export interface RememberVisibleRange {
  start: number;
  end: number;
}

export interface RememberReaderAnchor {
  momentId: string;
  withinRow: number;
  focusedMomentId: string | null;
}

export function buildRememberVirtualLayout(
  items: readonly StreamItem[],
  measuredHeights: ReadonlyMap<string, number>,
  estimateHeight: (item: StreamItem) => number,
): RememberVirtualLayout {
  const prefix = new Float64Array(items.length + 1);
  const years: number[] = [];
  const indexByKey = new Map<string, number>();
  for (let index = 0; index < items.length; index += 1) {
    const item = items[index];
    const measured = measuredHeights.get(item.key);
    const height =
      measured !== undefined && Number.isFinite(measured) && measured > 0
        ? measured
        : estimateHeight(item);
    prefix[index + 1] = prefix[index] + Math.max(1, height);
    indexByKey.set(item.key, index);
    if (item.kind === 'year') years.push(index);
  }
  return {
    items,
    prefix,
    yearIndices: Int32Array.from(years),
    indexByKey,
    total: prefix[items.length],
  };
}

function upperBound(
  values: ArrayLike<number>,
  target: number,
  metrics?: RememberVirtualMetrics,
): number {
  let low = 0;
  let high = values.length;
  while (low < high) {
    if (metrics) metrics.comparisons += 1;
    const middle = low + ((high - low) >> 1);
    if (values[middle] <= target) low = middle + 1;
    else high = middle;
  }
  return low;
}

function lowerBound(
  values: ArrayLike<number>,
  target: number,
  metrics?: RememberVirtualMetrics,
): number {
  let low = 0;
  let high = values.length;
  while (low < high) {
    if (metrics) metrics.comparisons += 1;
    const middle = low + ((high - low) >> 1);
    if (values[middle] < target) low = middle + 1;
    else high = middle;
  }
  return low;
}

export function rememberVisibleRange(
  layout: RememberVirtualLayout,
  localViewportTop: number,
  viewportHeight: number,
  overscan: number,
  metrics?: RememberVirtualMetrics,
): RememberVisibleRange {
  const count = layout.items.length;
  if (count === 0) return { start: 0, end: 0 };
  const top = Math.max(0, localViewportTop - overscan);
  const bottom = Math.max(top, localViewportTop + viewportHeight + overscan);
  const start = Math.max(0, Math.min(count, upperBound(layout.prefix, top, metrics) - 1));
  const end = Math.max(
    start,
    Math.min(count, lowerBound(layout.prefix, bottom, metrics) + 1),
  );
  return { start, end };
}

export function rememberStickyYear(
  layout: RememberVirtualLayout,
  localViewportTop: number,
  metrics?: RememberVirtualMetrics,
): number | null {
  if (layout.yearIndices.length === 0 || localViewportTop <= 0) return null;
  let low = 0;
  let high = layout.yearIndices.length;
  while (low < high) {
    if (metrics) metrics.comparisons += 1;
    const middle = low + ((high - low) >> 1);
    const itemIndex = layout.yearIndices[middle];
    if (layout.prefix[itemIndex] <= localViewportTop) low = middle + 1;
    else high = middle;
  }
  const yearPosition = low - 1;
  if (yearPosition < 0) return null;
  const itemIndex = layout.yearIndices[yearPosition];
  if (layout.prefix[itemIndex + 1] > localViewportTop) return null;
  const item = layout.items[itemIndex];
  return item.kind === 'year' ? item.year : null;
}

export function rememberReaderAnchor(
  layout: RememberVirtualLayout,
  localViewportTop: number,
  focusedMomentId: string | null = null,
): RememberReaderAnchor | null {
  if (layout.items.length === 0) return null;
  let index = Math.max(
    0,
    Math.min(
      layout.items.length - 1,
      upperBound(layout.prefix, Math.max(0, localViewportTop)) - 1,
    ),
  );
  while (index < layout.items.length && layout.items[index].kind === 'year') {
    index += 1;
  }
  if (index >= layout.items.length) return null;
  const item = layout.items[index];
  if (item.kind !== 'moment') return null;
  return {
    momentId: item.moment.id,
    withinRow: localViewportTop - layout.prefix[index],
    focusedMomentId,
  };
}

export function rememberAnchorScrollTop(
  layout: RememberVirtualLayout,
  anchor: RememberReaderAnchor,
  wrapperScrollOffset: number,
): number | null {
  const index = layout.indexByKey.get(anchor.momentId);
  if (index === undefined) return null;
  return Math.max(0, wrapperScrollOffset + layout.prefix[index] + anchor.withinRow);
}

// Quiet time after the last scroll event or touch end before a held-back
// anchor correction is written to scrollTop. iOS stops a running momentum
// glide on scrollTop writes, so ordinary corrections wait for this quiet.
export const REMEMBER_SCROLL_SETTLE_MS = 150;

// While a correction `pending` is held back, the stream is drawn `pending` px
// above its in-flow position. Near either end that would show rows over the
// header, a gap above the first row or an empty strip below the last row, so
// the correction is written even during a scroll. `localTop` is the viewport
// top in layout coordinates; `localTop - pending` is the same point measured
// from the stream's in-flow start.
export function rememberPendingNearEdge(
  pending: number,
  localTop: number,
  viewportHeight: number,
  total: number,
  margin: number,
): boolean {
  if (Math.abs(pending) < 1) return false;
  const inFlowTop = localTop - pending;
  const top = Math.min(localTop, inFlowTop);
  const bottom = Math.max(localTop, inFlowTop) + viewportHeight;
  return top < margin || bottom > total - margin;
}

export function rememberLayoutRetainedBytes(layout: RememberVirtualLayout): number {
  return layout.prefix.byteLength + layout.yearIndices.byteLength;
}
